<template>
  <div class="container" :class="{ bo_compact: compactMode }">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4" style="max-width:597px">
        <div>
          <div>
            <h3 v-if="hasMultiLocations()">Utsolgt hos {{ location.name }}</h3>
            <h3 v-else>Utsolgt</h3>

            <div v-if="soldOutItems.length > 0" class="mt-5 mb-5 soldOutContainer">
                <p>Følgende produkter er utsolgte. Trykk på knappen "Tilgjengelig" for å gjøre produkt tilgjengelig for salg igjen.</p>

                <div v-if="soldOutItems.length > 1" class="d-flex justify-content-start mb-3">
                  <button class="bo_btn_success" @click="unmarkAllAsSoldOut()"> Alle tilgjengelig </button>
                </div>

                <!-- A mark can outlive what it marks: a product deleted, a variant removed, a product no longer
                     sold here. Those rows used to have no name at all (osushi, 2026-10-09) — they are named
                     for what they are, sorted last, and offer «Fjern» rather than «Tilgjengelig». -->
                <div v-for="soldOutItem in sortedSoldOutItems" :key="soldOutItem.id">
                  <div class="item bo_soldout-row">
                    <div class="item-col-right">
                      <div class="flex-box">
                        <div v-if="soldOutState(soldOutItem) === 'gone'">
                          <span class="bo_soldout-stale">{{ getGoneLabel(soldOutItem) }}</span>
                        </div>
                        <div v-else>
                          <span class="item-title">{{ getSoldOutMainLabel(soldOutItem) }}</span>
                          <span v-if="getSoldOutParentName(soldOutItem)" class="bo_soldout-parent ms-1">{{ getSoldOutParentName(soldOutItem) }}</span>
                          <span v-if="soldOutItem.templateId" class="bo_count ms-1">(tilbehør)</span>
                          <span v-else-if="soldOutItem.subselectionId" class="bo_count ms-1">(variant)</span>
                          <div v-if="soldOutState(soldOutItem) === 'notHere'" class="bo_soldout-stale">
                            Selges ikke lenger hos {{ location.name }}
                          </div>
                        </div>
                        <div class="cart-buttons-panel">
                          <button v-if="soldOutState(soldOutItem) === 'ok'" class="bo_btn ms-3" @click="unmarkAsSoldOut(soldOutItem)"> Tilgjengelig </button>
                          <button v-else class="bo_btn_secondary ms-3" title="Fjerner merkingen. Menyen endres ikke."
                                  @click="unmarkAsSoldOut(soldOutItem)"> Fjern </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-if="staleSoldOutCount > 0" class="bo_soldout-note mt-3 mb-0">
                  Rader med «Fjern» er merket utsolgt for et produkt som er slettet, eller som ikke selges hos
                  {{ location.name }} lenger. De stopper ikke salget av noe — «Fjern» rydder bort merkingen, og
                  menyen endres ikke. Er retten bare midlertidig stengt her og fortsatt utsolgt, lar du raden stå.
                </p>
            </div>

            <p class="mt-4 mb-1">Nedenfor kan du velge produkter som du vil merke som utsolgte og trykke på knappen "Merk som utsolgt".</p>
            <!-- Only worth saying when something was actually left out, and only to a merchant that has
                 more than one location — otherwise it states the obvious. -->
            <p v-if="hasMultiLocations()" class="mb-3" style="color:var(--bo-muted); font-size:0.9rem">
              <span v-if="hiddenItemCount > 0">
                Viser kun produkter som selges hos <strong>{{ location.name }}</strong> —
                {{ hiddenItemCount }} produkt{{ hiddenItemCount === 1 ? '' : 'er' }} hører til andre avdelinger og er utelatt.
              </span>
              <!-- Said explicitly because the button below says "Merk som utsolgt" with no scope in it. -->
              <span :class="{ 'd-block': hiddenItemCount > 0 }">
                Utsolgt-merking gjelder <strong>kun {{ location.name }}</strong> — andre avdelinger påvirkes ikke.
              </span>
            </p>
            <div class="sticky-top soldOutButtonsStickyPanel d-flex flex-wrap align-items-center gap-2">
              <div class="position-relative flex-grow-1" style="min-width:180px">
                <input class="form-control" type="text" v-model="searchQuery" @input="search()"
                       placeholder="Søk etter produkt eller tilbehør..." style="padding-right:32px"/>
                <a v-if="searchQuery" class="bo_search-clear" title="Tøm søket" @click="clearSearch()">
                  <font-awesome-icon :icon="['fas', 'times']"/>
                </a>
              </div>
              <div class="flex-shrink-0 text-nowrap ms-auto">
                  <button type="button" class="bo_btn_secondary ms-1"
                          :title="compactMode ? 'Vis bilder og beskrivelser igjen' : 'Kompakt visning: skjuler bilder og beskrivelser'"
                          @click="compactMode = !compactMode">
                    <font-awesome-icon :icon="['fas', compactMode ? 'image' : 'list']"/>
                    {{ compactMode ? 'Full visning' : 'Kompakt visning' }}
                  </button>
                  <button v-if="selectionCount > 0" class="bo_btn ms-3" @click="markAsSoldOut()"> Merk som utsolgt ({{ selectionCount }})</button>
                  <button v-else class="bo_btn ms-3" disabled="disabled"> Merk som utsolgt </button>
              </div>
            </div>

            <div v-if="!isSearching" class="mt-3">
                <div v-for="itemsOfCategory in itemsOfCategories" :key="itemsOfCategory.id" class="bo_items-of-category">
                  <div class="bo_items_of_category_panel">
                    <span class="bo_items-of-category-header"> {{ itemsOfCategory.name }} </span>
                  </div>

                  <div v-for="item in itemsOfCategory.items" :key="item.id">
                    <ItemSoldOutRow :item="item" :selectedItems="selectedItems" :selectedSubselections="selectedSubselections"
                                    :soldOutItemIds="alreadySoldOutItemIds" :soldOutSubselectionIds="alreadySoldOutSubselectionIds"
                                    @toggleItem="updateSelectedItems" @toggleSubselection="toggleSubselection" @toggleAllSubselections="toggleAllSubselections"/>
                  </div>
                </div>
            </div>
            <div v-else class="mt-3">
              <div v-for="item in filteredItems" :key="item.id">
                <ItemSoldOutRow :item="item" :selectedItems="selectedItems" :selectedSubselections="selectedSubselections"
                                :soldOutItemIds="alreadySoldOutItemIds" :soldOutSubselectionIds="alreadySoldOutSubselectionIds"
                                @toggleItem="updateSelectedItems" @toggleSubselection="toggleSubselection" @toggleAllSubselections="toggleAllSubselections"/>
              </div>
            </div>

            <div v-if="displayedAddOnTemplates.length > 0" class="bo_items-of-category">
              <div class="bo_items_of_category_panel">
                <span class="bo_items-of-category-header"> Tilbehørsbibliotek </span>
              </div>
              <div v-for="addOnTemplate in displayedAddOnTemplates" :key="addOnTemplate.id">
                <div class="d-flex align-items-center justify-content-between bo_soldout-row"
                     :class="{ 'bo_clickable-row': !alreadySoldOutTemplateIds.includes(addOnTemplate.id) }"
                     @click="!alreadySoldOutTemplateIds.includes(addOnTemplate.id) && updateSelectedItems(addOnTemplate)">
                  <span class="item-title">{{ addOnTemplate.name }}</span>
                  <span v-if="alreadySoldOutTemplateIds.includes(addOnTemplate.id)" class="bo_sold-out-badge">Utsolgt</span>
                  <input v-else type="checkbox" :checked="selectedItemsContains(addOnTemplate)" class="multiple-checkbox bo_checkbox-display" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import DashboardService from "@/services/dashboard/DashboardService";
import ProductService from "@/services/product/ProductService";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import ItemSoldOutRow from "@/components/product/ItemSoldOutRow";
import LocationUtil from "@/util/LocationUtil";
import TextUtil from "@/util/TextUtil";
//import levenshtein from "js-levenshtein";

export default {
    components: {ConfirmDialogue, LoadingSpinner, ItemSoldOutRow},
    data() {
      return {
        loading: false,

        location: {},
        locationId: '',
        compactMode: localStorage.getItem('bo_products_compact') === 'true', //Shared density preference with the products page
        selectedItems: [],
        selectedSubselections: [], //Item variants selected to mark sold out
        items: [], //The products sold at this location — what can be marked
        allItems: [], //Every product the merchant has (deleted ones excepted) — what an existing mark is looked up in
        addOnTemplates: [],
        itemsOfCategories: [],
        soldOutItems: [],
        searchQuery: '',
        filteredItems: [],
        hiddenItemCount: 0 //Products the merchant has, but not at this location
      };
    },
    watch: {
      compactMode(newVal){
        localStorage.setItem('bo_products_compact', newVal ? 'true' : 'false');
      }
    },
    computed: {
      selectionCount(){
        return this.selectedItems.length + this.selectedSubselections.length;
      },
      sortedSoldOutItems(){
        //What is really sold out here first; marks left behind by a deleted or moved product last
        const rank = { ok: 0, notHere: 1, gone: 2 };
        return this.soldOutItems.slice().sort((a, b) =>
            (rank[this.soldOutState(a)] - rank[this.soldOutState(b)])
            || this.getSoldOutSortKey(a).localeCompare(this.getSoldOutSortKey(b)));
      },
      staleSoldOutCount(){
        return this.soldOutItems.filter(soldOutItem => this.soldOutState(soldOutItem) !== 'ok').length;
      },
      alreadySoldOutItemIds(){
        return this.soldOutItems.filter(s => s.itemId).map(s => s.itemId);
      },
      alreadySoldOutSubselectionIds(){
        return this.soldOutItems.filter(s => s.subselectionId).map(s => s.subselectionId);
      },
      alreadySoldOutTemplateIds(){
        return this.soldOutItems.filter(s => s.templateId).map(s => s.templateId);
      },
      isSearching(){
        return this.searchQuery.trim() !== '';
      },
      displayedAddOnTemplates(){
        //When searching, only the library add-ons whose name matches the start of a word; otherwise all of them
        if(!this.isSearching){
          return this.addOnTemplates;
        }
        return this.addOnTemplates.filter(addOnTemplate => this.matchesQuery(addOnTemplate.name));
      }
    },
    mounted() {
      this.locationId = this.$route.query.locationId;
      DashboardService.getDataForSoldOutAdminGui(this.locationId).then(
          (response) => {
            let location = response.data.location;
            let categories = response.data.categories;
            let items = response.data.items;
            let soldOutItems = response.data.soldOutItems;
            this.location = location;
            // The endpoint returns the merchant's WHOLE catalogue — only soldOutItems is scoped to the
            // location. Narrow it here, once, so both the category listing and the search see the same
            // set: marking a product sold out "hos <location>" is meaningless if it is not sold there.
            let locationCategories = categories.filter(category => this.availableAtThisLocation(category));
            let locationItems = items.filter(item => this.availableAtThisLocation(item));
            this.hiddenItemCount = items.length - locationItems.length;
            this.items = locationItems;
            this.allItems = items;
            this.addOnTemplates = response.data.addOnTemplates || [];
            this.itemsOfCategories = this.getItemsOfCategories(locationCategories, locationItems);
            this.soldOutItems = soldOutItems;
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      getItem(itemId){
        //Every product, not only this location's: a mark for one no longer sold here still needs its name
        for (let i = 0; i < this.allItems.length; i++) {
          let item = this.allItems[i];
          if(item.id == itemId){
            return item;
          }
        }
        return null;
      },
      getItemName(itemId){
          let item = this.getItem(itemId);
          if(item != null){
            return item.name;
          }
          return '';
      },
      getItemMenuNr(itemId){
        let item = this.getItem(itemId);
        if(item != null){
          return item.menuNr;
        }
        return '';
      },
      getTemplateName(templateId){
        for (let i = 0; i < this.addOnTemplates.length; i++) {
          if(this.addOnTemplates[i].id == templateId){
            return this.addOnTemplates[i].name;
          }
        }
        return '';
      },
      getSubselection(subselectionId){
        //Search every item's variants for the one with this id, return it with its parent item
        for (let i = 0; i < this.allItems.length; i++) {
          let item = this.allItems[i];
          if(item.subselections){
            for (let s = 0; s < item.subselections.length; s++) {
              if(item.subselections[s].id == subselectionId){
                return { name: item.subselections[s].name, itemName: item.name, item: item };
              }
            }
          }
        }
        return null;
      },
      /**
       * What a sold-out mark still means here: 'ok' — it marks something sold at this location; 'notHere' —
       * a product (or its variant) that exists but is not sold here any more; 'gone' — nothing: the product was
       * deleted, the variant removed from it, or the library add-on deleted.
       */
      soldOutState(soldOutItem){
        if(soldOutItem.templateId){
          return this.addOnTemplates.some(template => template.id == soldOutItem.templateId) ? 'ok' : 'gone';
        }
        let item = soldOutItem.subselectionId
            ? (this.getSubselection(soldOutItem.subselectionId) || {}).item
            : this.getItem(soldOutItem.itemId);
        if(!item){
          return 'gone';
        }
        return this.availableAtThisLocation(item) ? 'ok' : 'notHere';
      },
      getGoneLabel(soldOutItem){
        if(soldOutItem.templateId){
          return 'Slettet tilbehør';
        }
        return soldOutItem.subselectionId ? 'Slettet variant' : 'Slettet produkt';
      },
      getSoldOutLabel(soldOutItem){
        //A sold-out record is a product (itemId), a library add-on (templateId) or an item variant (subselectionId)
        if(soldOutItem.templateId){
          return this.getTemplateName(soldOutItem.templateId);
        }
        if(soldOutItem.subselectionId){
          let subselection = this.getSubselection(soldOutItem.subselectionId);
          return subselection ? (subselection.name.trim() + ' (' + subselection.itemName.trim() + ')') : '';
        }
        return (this.getItemMenuNr(soldOutItem.itemId) + '  ' + this.getItemName(soldOutItem.itemId)).trim();
      },
      getSoldOutMainLabel(soldOutItem){
        //The entry's own name (variant / addon / product), without the parent item suffix
        if(soldOutItem.templateId){
          return this.getTemplateName(soldOutItem.templateId);
        }
        if(soldOutItem.subselectionId){
          let subselection = this.getSubselection(soldOutItem.subselectionId);
          return subselection ? subselection.name.trim() : '';
        }
        return (this.getItemMenuNr(soldOutItem.itemId) + '  ' + this.getItemName(soldOutItem.itemId)).trim();
      },
      getSoldOutParentName(soldOutItem){
        //For a variant, the parent product it belongs to (shown in a muted style)
        if(soldOutItem.subselectionId){
          let subselection = this.getSubselection(soldOutItem.subselectionId);
          return subselection ? subselection.itemName.trim() : '';
        }
        return '';
      },
      getSoldOutSortKey(soldOutItem){
        //Group sold-out records by their parent (item/template), then by label, so variants of the
        //same item end up right below each other.
        let parentName = '';
        if(soldOutItem.subselectionId){
          let subselection = this.getSubselection(soldOutItem.subselectionId);
          parentName = subselection ? subselection.itemName.trim() : '';
        } else if(soldOutItem.templateId){
          parentName = this.getTemplateName(soldOutItem.templateId).trim();
        } else if(soldOutItem.itemId){
          parentName = this.getItemName(soldOutItem.itemId).trim();
        }
        return parentName.toLowerCase() + ' ' + this.getSoldOutLabel(soldOutItem).toLowerCase();
      },
      /**
       * Whether a category or item is offered at the location this page is for.
       *
       * An empty or missing availableAtLocationIds is treated as "everywhere": that is old data from
       * before the field existed, and hiding a product the merchant can genuinely sell here would be
       * worse than showing one they cannot.
       */
      availableAtThisLocation(categoryOrItem){
        let locationIds = categoryOrItem.availableAtLocationIds;
        if(!Array.isArray(locationIds) || locationIds.length === 0){
          return true;
        }
        return locationIds.indexOf(this.locationId) > -1;
      },
      getItemsOfCategories(categories, items){
        let itemsOfCategories = [];
        for (var i = 0; i < categories.length; i++) {
          var category = categories[i];
          var tempItems = [];
          for (var x = 0; x < items.length; x++) {
            var item = items[x];
            if(item.categoryId == category.id){
              tempItems.push(item)
            }
          }
          //Remove already found items of category
          items = items.filter(function(item) {
            return tempItems.indexOf(item) === -1;
          });

          //Sort tempItems by asc sortOrder
          tempItems.sort(function(a, b) {
            if(a.sortOrder == b.sortOrder) { return 0; }
            if(a.sortOrder > b.sortOrder) {
              return 1;
            }else {
              return -1;
            }
          });

          let itemsOfCategory = {
            id  : category.id,
            sortOrder: category.sortOrder,
            name : category.name,
            items : tempItems
          };
          itemsOfCategories.push(itemsOfCategory);
        }

        //Sort itemsOfCategory by asc sortOrder
        itemsOfCategories.sort(function(a, b) {
          if(a.sortOrder == b.sortOrder) { return 0; }
          if(a.sortOrder > b.sortOrder) {
            return 1;
          }else {
            return -1;
          }
        });

        return itemsOfCategories;
      },
      selectedItemsContains(Item){
        for (let i = 0; i < this.selectedItems.length; i++) {
          let selectedItem = this.selectedItems[i];
          if(selectedItem.id == Item.id){
            return true;
          }
        }
        return false;
      },
      updateSelectedItems(item){
        if(this.selectedItemsContains(item)){
          //Already selected, remove it
          for (let i = 0; i < this.selectedItems.length; i++) {
            if (this.selectedItems[i].id == item.id) {
              this.selectedItems.splice(i, 1);
              break;
            }
          }
        }else {
          //Add it
          this.selectedItems.push(item);
        }
      },
      toggleSubselection(subselection){
        for (let i = 0; i < this.selectedSubselections.length; i++) {
          if(this.selectedSubselections[i].id == subselection.id){
            this.selectedSubselections.splice(i, 1);
            return;
          }
        }
        this.selectedSubselections.push(subselection);
      },
      toggleAllSubselections(item){
        //Only the variants that are not already sold out can be marked
        let subselections = (item.subselections || []).filter(sub => !this.alreadySoldOutSubselectionIds.includes(sub.id));
        let allSelected = subselections.every(sub => this.selectedSubselections.some(s => s.id == sub.id));
        if(allSelected){
          //All variants selected, unselect all of them
          this.selectedSubselections = this.selectedSubselections.filter(s => !subselections.some(sub => sub.id == s.id));
        } else {
          //Select all variants that are not already selected
          subselections.forEach(sub => {
            if(!this.selectedSubselections.some(s => s.id == sub.id)){
              this.selectedSubselections.push(sub);
            }
          });
        }
      },
      unmarkAsSoldOut(soldOutItem){
        this.loading = true;
        ProductService.unmarkProductsAsSoldOut(soldOutItem.merchant, soldOutItem.id).then(
            (response) => {
              if(response.data != null){
                this.$router.go(); //Reload current page
              }else {
                console.log("unmarkProductsAsSoldOut failed");
              }
            },
            (error) => {
              console.log(error);
            }
        );
      },
      async unmarkAllAsSoldOut(){
        //Make every sold-out record (product, variant and library add-on) available again in one go,
        //instead of clicking "Tilgjengelig" on each row.
        const ok = await this.$refs.confirmDialogue.show({
          title: this.getSoldOutTittel(),
          message: 'Vil du gjøre alle utsolgte produkter tilgjengelige igjen?',
          okButton: 'Ja, gjør alle tilgjengelig',
          cancelButton: 'Avbryt'
        });
        if(!ok){
          return;
        }
        this.loading = true;
        let requests = this.soldOutItems.map(soldOutItem =>
            ProductService.unmarkProductsAsSoldOut(soldOutItem.merchant, soldOutItem.id));
        Promise.all(requests).then(
            () => {
              this.$router.go(); //Reload current page
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      async markAsSoldOut(){
        const ok = await this.$refs.confirmDialogue.show({
          title: this.getSoldOutTittel(),
          message: this.getSoldOutProductsText(),
          okButton: 'Ja, merk som utsolgt',
          cancelButton: 'Avbryt'
        })
        if(ok) {
          this.loading = true;

          //The selection can contain both products (type item) and library add-ons (type add-on_template)
          let selectedProducts = this.selectedItems.filter(selected => selected.type !== 'add-on_template');
          let selectedTemplates = this.selectedItems.filter(selected => selected.type === 'add-on_template');

          let requests = [];
          if(selectedProducts.length > 0){
            requests.push(ProductService.markProductsAsSoldOut({ locationId: this.locationId, items: selectedProducts }));
          }
          if(selectedTemplates.length > 0){
            requests.push(ProductService.markAddOnTemplatesAsSoldOut({ locationId: this.locationId, addOnTemplates: selectedTemplates }));
          }
          if(this.selectedSubselections.length > 0){
            requests.push(ProductService.markSubselectionsAsSoldOut({ locationId: this.locationId, subselections: this.selectedSubselections }));
          }

          Promise.all(requests).then(
              () => {
                this.$router.push({ path: 'overview' });
              },
              (error) => {
                console.log(error);
              }
          );
        } else {
          //Cancel, do nothing
        }
      },
      getSoldOutTittel(){
        if(this.hasMultiLocations()){
            return 'Utsolgt hos ' + this.location.name;
        }else {
            return 'Utsolgt';
        }
      },
      getSoldOutProductsText(){
        //A variant names its product, as in the sold-out list at the top: «2 stk vårruller med salat» on
        //its own does not say which dish ran out. Names are escaped — the dialog renders this as HTML.
        let text = '<div class="mb-3">Vil du merke følgende som utsolgte?</div>';
        for (let i = 0; i < this.selectedItems.length; i++) {
          let selected = this.selectedItems[i];
          text = text + '<strong>' + TextUtil.escapeHtml(selected.name) + '</strong>'
              + (selected.type === 'add-on_template' ? ' <span class="bo_count">(tilbehør)</span>' : '') + '<br/>';
        }
        for (let i = 0; i < this.selectedSubselections.length; i++) {
          let subselection = this.getSubselection(this.selectedSubselections[i].id);
          text = text + '<strong>' + TextUtil.escapeHtml(this.selectedSubselections[i].name) + '</strong>'
              + (subselection ? ' <span style="display:inline-block; color:var(--bo-muted); font-style:italic">' + TextUtil.escapeHtml(subselection.itemName.trim()) + '</span>' : '')
              + '<br/>';
        }
        return text
      },
      clearSearch() {
        this.searchQuery = '';
        this.search();
      },
      matchesQuery(name){
        //Match the query at the start of any word, so it can span several words.
        //"ris" matches "Kokt ris..." (not "Crispy"), "dumplings fylt" matches "8 stk dumplings fylt med...".
        if(!name){
          return false;
        }
        let q = this.searchQuery.trim().toLowerCase().replace(/\s+/g, ' ');
        if(!q){
          return false;
        }
        let lowerName = name.toLowerCase().replace(/\s+/g, ' ');
        //Word start positions: index 0 and every position right after a space
        let startPositions = [0];
        for(let i = 0; i < lowerName.length; i++){
          if(lowerName[i] === ' '){
            startPositions.push(i + 1);
          }
        }
        return startPositions.some(pos => lowerName.startsWith(q, pos));
      },
      matchesItem(item){
        //Match on the item name or any of its variant names
        if(this.matchesQuery(item.name)){
          return true;
        }
        if(item.subselections){
          return item.subselections.some(subselection => this.matchesQuery(subselection.name));
        }
        return false;
      },
      search() {
        if(this.searchQuery.trim() == ''){
          this.filteredItems = [];
        } else {
          this.filteredItems = this.items.filter(item => this.matchesItem(item));
        }
      }
    }
  };
</script>

<style scoped>
/* Vertically center the row content (name + checkbox/button). The global [class*="col-"] rule in
   backoffice.css gives this column padding-bottom: 15px (its name contains "col-"), which sat the name and
   the Tilgjengelig button at the top of the row with all the space below — the row itself
   (.bo_soldout-row) carries the vertical padding instead, equally above and below. */
.item-col-right{
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}
.cart-buttons-panel{
  align-items: center;
}
/* The whole row is clickable, the checkbox just shows the state */
.bo_clickable-row{
  cursor: pointer;
}
.bo_checkbox-display{
  pointer-events: none;
  margin: 0;
  vertical-align: middle;
}
/* .item.bo_soldout-row (0,3,0) so compact view's global `.bo_compact .item { padding: 2px }` cannot squeeze
   the row against its button — the same rhythm as the product rows below (.bo_soldout-item-row). */
.item.bo_soldout-row{
  border-bottom: 1px solid var(--bo-muted-2);
  padding-top: 15px;
  padding-bottom: 15px;
  padding-right: 5px;
}
/* The parent product a sold-out variant belongs to */
.bo_soldout-parent{
  color: var(--bo-muted);
  font-style: italic;
}
/* A mark for something deleted or no longer sold here — muted, so the real ones stand out */
.bo_soldout-stale{
  color: var(--bo-muted);
  font-style: italic;
}
.bo_soldout-note{
  color: var(--bo-muted);
  font-size: 0.9rem;
}
</style>
